import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatCurrency, formatDate, formatThousands } from "@/utils";

const SalesOrderPrint = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data, isLoading, error } = useFetchData(`sales-orders/${id}/show`);

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate(`/sales-order/${id}/detail`)}
                >
                    Kembali ke Detail
                </Button>
            </div>
        );
    }

    const order = data;
    const items = order?.items || [];

    const grandTotal = items.reduce((sum, item) => {
        const subtotal = item.quantity * item.price - (item.discount || 0);
        return sum + subtotal;
    }, 0);

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate(`/sales-order/${id}/detail`)}
                >
                    Kembali ke Detail
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Faktur_${order?.order_number || "Penjualan"}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="FAKTUR PENJUALAN" />

                {/* Order Info */}
                <div className="mb-4 grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                    <div className="flex">
                        <span className="w-32 font-medium">No. Order</span>
                        <span>: {order?.order_number}</span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">Tanggal</span>
                        <span>: {formatDate(order?.order_date)}</span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">Customer</span>
                        <span>: {order?.customer?.name || "-"}</span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">Metode Bayar</span>
                        <span>: {order?.payment_method?.name || "-"}</span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">
                            Status Pembayaran
                        </span>
                        <span>: {order?.payment_status?.name || "-"}</span>
                    </div>
                </div>

                {/* Items Table */}
                <table className="mb-4 w-full border-collapse border border-black text-xs">
                    <thead>
                        <tr className="bg-default-100">
                            <th className="border border-black p-1.5 text-center">
                                No.
                            </th>
                            <th className="border border-black p-1.5 text-left">
                                Barang
                            </th>
                            <th className="border border-black p-1.5 text-center">
                                Satuan
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Jumlah
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Harga
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Diskon
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Subtotal
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const subtotal =
                                item.quantity * item.price -
                                (item.discount || 0);
                            return (
                                <tr key={item.id}>
                                    <td className="border border-black p-1.5 text-center">
                                        {index + 1}.
                                    </td>
                                    <td className="border border-black p-1.5">
                                        {item.alias ||
                                            item.material?.name ||
                                            "-"}
                                    </td>
                                    <td className="border border-black p-1.5 text-center">
                                        {item.unit?.name || "-"}
                                    </td>
                                    <td className="border border-black p-1.5 text-right">
                                        {formatThousands(item.quantity)}
                                    </td>
                                    <td className="border border-black p-1.5 text-right">
                                        {formatCurrency(item.price)}
                                    </td>
                                    <td className="border border-black p-1.5 text-right">
                                        {formatCurrency(item.discount || 0)}
                                    </td>
                                    <td className="border border-black p-1.5 text-right">
                                        {formatCurrency(subtotal)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="bg-default-100 font-bold">
                            <td
                                colSpan={6}
                                className="border border-black p-1.5 text-right"
                            >
                                TOTAL
                            </td>
                            <td className="border border-black p-1.5 text-right">
                                {formatCurrency(grandTotal)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* Keterangan */}
                {order?.description && (
                    <div className="mb-6 text-xs">
                        <span className="font-medium">Keterangan: </span>
                        {order.description}
                    </div>
                )}

                {/* Signature area */}
                <div className="mt-12 grid grid-cols-2 gap-8 text-xs">
                    <div className="text-center">
                        <p className="font-medium">Penjual</p>
                        <div className="mt-16">
                            (.................................)
                        </div>
                    </div>
                    <div className="text-center">
                        <p className="font-medium">Customer</p>
                        <div className="mt-16">
                            (.................................)
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-8 space-y-3 text-xs text-default-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                    <div className="flex flex-col gap-1">
                        <p className="font-medium">Syarat & Ketentuan:</p>
                        <ol className="list-disc space-y-0.5 pl-4">
                            <li>
                                Barang yang sudah dibeli tidak dapat
                                dikembalikan kecuali ada kesepakatan tertulis.
                            </li>
                            <li>
                                Pembayaran harus dilakukan sesuai dengan metode
                                dan tenggat waktu yang telah disepakati.
                            </li>
                            <li>
                                Harga yang tertera sudah termasuk
                                potongan/diskon yang berlaku.
                            </li>
                            <li>
                                Klaim atas kerusakan atau ketidaksesuaian barang
                                harus diajukan maksimal 1x24 jam setelah barang
                                diterima.
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesOrderPrint;
